import React from 'react';
import { 
  Award, 
  Users, 
  Clock, 
  Shield,
  Target,
  Heart,
  CheckCircle
} from 'lucide-react';

const About = () => {
  const achievements = [
    {
      icon: <Award className="w-7 h-7 text-amber-600" />,
      ring: "bg-amber-50 ring-1 ring-amber-200",
      number: "15+",
      label: "سنة خبرة",
      description: "في مجال التحاليل الطبية"
    },
    {
      icon: <Users className="w-7 h-7 text-blue-600" />,
      ring: "bg-blue-50 ring-1 ring-blue-200",
      number: "50K+",
      label: "عميل راضٍ",
      description: "يثقون في خدماتنا"
    },
    {
      icon: <Clock className="w-7 h-7 text-teal-600" />,
      ring: "bg-teal-50 ring-1 ring-teal-200",
      number: "24/7",
      label: "خدمة مستمرة",
      description: "طوال أيام الأسبوع"
    },
    {
      icon: <Shield className="w-7 h-7 text-purple-600" />,
      ring: "bg-purple-50 ring-1 ring-purple-200",
      number: "100%",
      label: "دقة النتائج",
      description: "بأحدث التقنيات"
    }
  ];

  const values = [
    {
      icon: <Target className="w-5 h-5 text-blue-600" />,
      ring: "bg-blue-50",
      title: "الدقة والجودة",
      description: "نلتزم بأعلى معايير الجودة في جميع التحاليل"
    },
    {
      icon: <Heart className="w-5 h-5 text-rose-600" />,
      ring: "bg-rose-50",
      title: "الرعاية الشخصية",
      description: "نهتم بكل عميل ونقدم خدمة شخصية متميزة"
    },
    {
      icon: <Shield className="w-5 h-5 text-teal-600" />,
      title: "السرية والأمان",
      ring: "bg-teal-50",
      description: "نحافظ على سرية المعلومات الطبية بأقصى درجات الأمان"
    }
  ];

  const certifications = [
    "ISO 9001:2015",
    "CAP معتمد من",
    "وزارة الصحة المصرية",
    "الهيئة المصرية للتخصصات الصحية"
  ];

  return (
    <section id="about" className="py-24 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 text-teal-700 text-sm font-semibold px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
            حكايتنا
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-5 tracking-tight">
            من نحن
          </h2>
          <div className="w-16 h-1 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            معمل النخبه هو مؤسسة طبية رائدة في مجال التحاليل المخبرية، نقدم خدمات متميزة
            بأحدث التقنيات وأعلى معايير الجودة لضمان صحتك وراحة بالك
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Content */}
          <div className="space-y-7">
            <div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">
                رؤيتنا ورسالتنا
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                نسعى لأن نكون المرجع الأول في مجال التحاليل الطبية في المنطقة، من خلال تقديم
                خدمات طبية متطورة تساهم في الكشف المبكر عن الأمراض والحفاظ على صحة المجتمع.
              </p>
              <p className="text-gray-600 leading-relaxed">
                رسالتنا هي تقديم تحاليل طبية دقيقة وموثوقة بأسرع وقت ممكن، مع الحفاظ على
                أعلى معايير الجودة والسرية، وتوفير تجربة مريحة ومتميزة لجميع عملائنا.
              </p>
            </div>

            {/* Values */}
            <div className="space-y-4 pt-2">
              <h4 className="text-xl font-semibold text-blue-900">قيمنا الأساسية</h4>
              <div className="space-y-4">
                {values.map((value, index) => (
                  <div key={index} className="flex items-start gap-4 rtl:space-x-reverse">
                    <div className={`shrink-0 p-2.5 rounded-xl ${value.ring}`}>
                      {value.icon}
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">{value.title}</h5>
                      <p className="text-gray-600 text-sm">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="absolute -inset-3 rounded-[1.75rem] bg-gradient-to-br from-blue-600/15 to-teal-500/15 -z-10"></div>
            <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
              <img
                src="https://images.unsplash.com/photo-1579165466991-467135ad3110?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="معدات المختبر الطبي"
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="relative mb-24">
          {/* Signature: cardiac monitor pulse-line running through the stats */}
          <svg
            className="hidden lg:block absolute inset-x-0 top-14 w-full h-10 -z-0 opacity-70 pointer-events-none"
            viewBox="0 0 400 40"
            preserveAspectRatio="none"
          >
            <pattern id="pulseLine" width="100" height="40" patternUnits="userSpaceOnUse">
              <path
                d="M0,20 L34,20 L40,6 L46,34 L52,20 L100,20"
                fill="none"
                stroke="#0d9488"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </pattern>
            <rect width="400" height="40" fill="url(#pulseLine)" />
          </svg>

          <div className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl shadow-lg text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100"
              >
                <div className={`w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center ${achievement.ring}`}>
                  {achievement.icon}
                </div>
                <div className="text-4xl font-extrabold text-blue-900 mb-1 tracking-tight">
                  {achievement.number}
                </div>
                <div className="text-base font-semibold text-gray-800 mb-1">
                  {achievement.label}
                </div>
                <div className="text-sm text-gray-500">
                  {achievement.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-100">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-blue-900 mb-3">
              الاعتمادات والشهادات
            </h3>
            <p className="text-gray-600">
              نحن معتمدون من أهم الجهات المحلية والدولية في مجال التحاليل الطبية
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center justify-center">
                <span className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition-colors duration-300 hover:bg-blue-600 hover:border-blue-600 hover:text-white">
                  <CheckCircle className="w-4 h-4 text-teal-600 group-hover:text-white" />
                  {cert}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
